import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '../../helpers/shadcnUtils.helper';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface Option {
  value: number;
  label: string;
}

interface SearchableSelectProps {
  fetchData: (query: string) => Promise<{ options: Option[] }>;
  label: string;
}

export interface SearchableSelectRef {
  clear: () => void;
  search: (term: string) => void;
}

export const SearchableSelect = forwardRef<
  SearchableSelectRef,
  SearchableSelectProps
>(({ fetchData, label }, ref) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [data, setData] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // Controls Popover visibility

  /**
   * Fetch data based on the search term
   */
  const loadData = useCallback(async () => {
    if (!searchTerm.trim()) {
      setData([]);
      return;
    }

    setLoading(true);
    try {
      const result = await fetchData(searchTerm);
      setData(result.options || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, fetchData]);

  /**
   * Debounce the search term updates
   */
  useEffect(() => {
    if (!searchTerm.trim()) {
      setData([]);
      return;
    }

    const debounceTimeout = setTimeout(() => {
      loadData();
    }, 300); // Debounce by 300ms

    return () => clearTimeout(debounceTimeout);
  }, [searchTerm, loadData]);

  /**
   * Expose public methods via ref
   */
  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchTerm('');
      setData([]);
      setIsOpen(false);
    },
    search: (term: string) => {
      setSearchTerm(term);
      setIsOpen(true);
    },
  }));

  /**
   * Handle focus and blur events
   */
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    setIsFocused(false);
    setTimeout(() => setIsOpen(false), 200); // Delay to allow clicks on items
  };

  /**
   * Handle when Popover should open
   */
  useEffect(() => {
    setIsOpen(isFocused || data.length > 0 || loading);
  }, [isFocused, data.length, loading]);

  return (
    <div className="relative w-full">
      <Command>
        <div className="relative">
          {/* Search Input */}

          {/* Loading Spinner */}
          {loading && (
            <div className="absolute transform -translate-y-1/2 right-2 top-1/2">
              <span className="w-4 h-4 border-t-2 border-b-2 border-gray-400 rounded-full animate-spin"></span>
            </div>
          )}
        </div>

        {/* Popover for Dropdown */}
        <Popover open={isFocused}>
          <PopoverTrigger asChild>
            <div className="relative" cmdk-input-wrapper="">
              {/* Uncomment if you need an icon */}
              {/* <MagnifyingGlassIcon className="w-4 h-4 mr-2 opacity-50 shrink-0" /> */}
              <CommandPrimitive.Input
                value={searchTerm}
                className={cn(
                  'peer h-14 block w-full px-3 pt-4 pb-1 text-base bg-neutral-98 hover:bg-blue-95 rounded-xl focus:border focus:border-primary focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                )}
                onValueChange={setSearchTerm}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
              {label && (
                <label
                  className={cn(
                    'absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-40 transition-all duration-300 transform origin-left scale-100 peer-placeholder-shown:top-1/2 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:scale-100 peer-focus:!top-1 peer-focus:translate-y-0 peer-focus:scale-75',
                    searchTerm ? '!top-1 translate-y-0 !scale-75' : '',
                  )}
                >
                  {label}
                </label>
              )}
            </div>
            {/* <CommandInput
              label={label}
              value={searchTerm}

            /> */}
          </PopoverTrigger>

          <PopoverContent className="p-0 overflow-hidden border rounded-md shadow-lg ">
            <CommandList>
              {loading && <CommandEmpty>Loading...</CommandEmpty>}
              {!loading && data.length === 0 && (
                <CommandEmpty>No results found.</CommandEmpty>
              )}
              {data.map((item) => (
                <CommandItem
                  key={item.value}
                  onSelect={() => {
                    setIsOpen(false); // Close after selection
                  }}
                  className="p-2 cursor-pointer hover:bg-gray-200"
                >
                  {item.label}
                </CommandItem>
              ))}
            </CommandList>
          </PopoverContent>
        </Popover>
      </Command>
    </div>
  );
});

// Set a display name for better debugging
SearchableSelect.displayName = 'SearchableSelect';
